/**
 * SplitPathRenderer - Renders drawing paths split at snap point
 *
 * Splits path into pre-snap (dashed) and post-snap (normal or dashed) portions.
 * Used for pre-snap motion and shift indicators.
 *
 * Pure visual component - no state or handlers.
 */

import type { Drawing } from '@/types/Drawing.types'
import type { PathSplitModifier } from './Types'
import { PreSnapIndicator } from './PreSnapIndicator'
import { splitPathAtPoint, getPathTangentAtPoint } from '@/utils/DrawingPath.utils'
import { buildFilledArrow, buildFilledTShape } from '@/utils/SvgPath.utils'

interface SplitPathRendererProps {
	drawing: Drawing
	modifier: PathSplitModifier
	coordSystem: {
		feetToPixels: (x: number, y: number) => { x: number; y: number }
		pixelsToFeet: (x: number, y: number) => { x: number; y: number }
		scale: number
	}
}

/**
 * Renders path split into pre-snap and post-snap portions
 *
 * @param drawing - The resolved drawing data
 * @param modifier - Path split modifier configuration
 *
 * Pre-snap portion is always dashed.
 * Post-snap portion is dashed for shift, normal for motion.
 */
export function SplitPathRenderer({ drawing, modifier, coordSystem }: SplitPathRendererProps) {
	// Use the indicator position as the split point coordinate
	const { preSnapPath, postSnapPath } = splitPathAtPoint(drawing, modifier.indicator.position, coordSystem)
	// Calculate tangent angle for perpendicular line orientation
	const tangentAngle = getPathTangentAtPoint(drawing, modifier.indicator.position, coordSystem)

	const { color, strokeWidth } = drawing.style
	const { preSnapStyle, postSnapStyle, indicator } = modifier

	return (
		<g className="split-path">
			{/* Pre-snap: always dashed */}
			{preSnapPath && (
				<path
					d={preSnapPath}
					stroke={color}
					strokeWidth={strokeWidth}
					strokeDasharray="4 4"
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>
			)}

			{/* Post-snap: dashed (shift) or normal (motion) */}
			{postSnapPath && (
				<path
					d={postSnapPath}
					stroke={color}
					strokeWidth={strokeWidth}
					strokeDasharray={postSnapStyle === 'dashed' ? '4 4' : undefined}
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>
			)}

			{/* Filled line ending on post-snap only (if normal) */}
			{postSnapStyle === 'normal' && drawing.style.lineEnd !== 'none' && (() => {
				const pointIds = Object.keys(drawing.points)
				if (pointIds.length === 0) return null

				const lastPointId = pointIds[pointIds.length - 1]
				if (!lastPointId) return null
				const lastPoint = drawing.points[lastPointId]
				if (!lastPoint) return null

				const endPoint = coordSystem.feetToPixels(lastPoint.x, lastPoint.y)
				const endAngle = tangentAngle + Math.PI

				const shapes = drawing.style.lineEnd === 'arrow'
					? buildFilledArrow(endPoint, endAngle, strokeWidth)
					: buildFilledTShape(endPoint, endAngle, strokeWidth)

				return (
					<>
						{shapes.map((d, i) => (
							<path key={`split-ending-${i}`} d={d} fill={color} />
						))}
					</>
				)
			})()}

			{/* Snap point indicator */}
			<PreSnapIndicator position={indicator.position} motionType={indicator.motionType} tangentAngle={tangentAngle} />
		</g>
	)
}
